import { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { Clock, Users, Video, LogIn } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";

interface WaitingRoomProps {
  meetingId: string;
  userId: string;
  guestName?: string;
  onAdmitted: () => void;
  onDenied: () => void;
}

export default function WaitingRoom({ 
  meetingId, 
  userId, 
  guestName,
  onAdmitted, 
  onDenied 
}: WaitingRoomProps) {
  const navigate = useNavigate();
  const [queuePosition, setQueuePosition] = useState<number>(0);
  const [totalWaiting, setTotalWaiting] = useState<number>(0);
  const [status, setStatus] = useState<'waiting' | 'admitted' | 'denied'>('waiting');
  const [isGuest, setIsGuest] = useState(false);

  useEffect(() => {
    // Check if this is a guest user
    const checkUserType = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setIsGuest(!session?.user);
    };
    checkUserType();
    // Add user to waiting room queue
    const joinWaitingRoom = async () => {
      const { data, error } = await supabase
        .from('waiting_room_queue')
        .insert({
          meeting_id: meetingId,
          user_id: userId,
          guest_name: guestName || null,
          status: 'waiting'
        })
        .select()
        .single();

      if (error) {
        console.error('Error joining waiting room:', error);
        return;
      }

      // Fetch queue position
      const { count } = await supabase
        .from('waiting_room_queue')
        .select('*', { count: 'exact', head: true })
        .eq('meeting_id', meetingId)
        .eq('status', 'waiting')
        .lt('joined_at', data.joined_at);

      setQueuePosition((count || 0) + 1);
    };

    joinWaitingRoom();

    // Subscribe to waiting room updates
    const channel = supabase
      .channel(`waiting-room-${meetingId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'waiting_room_queue',
          filter: `user_id=eq.${userId}`
        },
        (payload) => {
          if (payload.eventType === 'UPDATE') {
            const newStatus = payload.new.status;
            setStatus(newStatus);
            
            if (newStatus === 'admitted') {
              onAdmitted();
            } else if (newStatus === 'denied') {
              onDenied();
            }
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'waiting_room_queue',
          filter: `meeting_id=eq.${meetingId}`
        },
        async () => {
          // Update queue count
          const { count } = await supabase
            .from('waiting_room_queue')
            .select('*', { count: 'exact', head: true })
            .eq('meeting_id', meetingId)
            .eq('status', 'waiting');
          
          setTotalWaiting(count || 0);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [meetingId, userId, guestName, onAdmitted, onDenied]);

  const handleLeave = async () => {
    await supabase
      .from('waiting_room_queue')
      .delete()
      .eq('meeting_id', meetingId)
      .eq('user_id', userId);
    
    window.history.back();
  };

  if (status === 'denied') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader>
            <CardTitle className="text-center text-destructive">Access Denied</CardTitle>
            <CardDescription className="text-center">
              The host has denied your request to join this meeting.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex justify-center">
            <Button onClick={handleLeave} variant="outline">
              Leave
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="max-w-md w-full">
        <CardHeader>
          <div className="flex justify-center mb-4">
            <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
              <Clock className="h-8 w-8 text-primary animate-pulse" />
            </div>
          </div>
          <CardTitle className="text-center">Waiting for Host</CardTitle>
          <CardDescription className="text-center">
            You're in the waiting room. The host will let you in soon.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Status Cards */}
          <div className="grid grid-cols-2 gap-4">
            <Card>
              <CardContent className="pt-6 pb-4">
                <div className="flex flex-col items-center gap-2">
                  <Users className="h-5 w-5 text-muted-foreground" />
                  <div className="text-2xl font-bold">{queuePosition}</div>
                  <p className="text-xs text-muted-foreground">Your Position</p>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6 pb-4">
                <div className="flex flex-col items-center gap-2">
                  <Video className="h-5 w-5 text-muted-foreground" />
                  <div className="text-2xl font-bold">{totalWaiting}</div>
                  <p className="text-xs text-muted-foreground">Total Waiting</p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Info Text */}
          <div className="text-center text-sm text-muted-foreground space-y-2">
            <p>Please wait while the host reviews your request.</p>
            <p>Keep this window open to join automatically.</p>
            
            {isGuest && (
              <div className="mt-4 p-3 bg-muted/50 rounded-lg">
                <p className="font-medium text-foreground">Are you the host?</p>
                <p className="text-xs mt-1">If you created this meeting, please log in to bypass the waiting room.</p>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col gap-2 pt-4">
            {isGuest && (
              <Button 
                onClick={() => navigate(`/auth?redirect=/meeting/${meetingId}`)} 
                className="w-full"
              >
                <LogIn className="h-4 w-4 mr-2" />
                Login as Host
              </Button>
            )}
            <Button onClick={handleLeave} variant="outline" className="w-full">
              Leave Waiting Room
            </Button>
          </div>

          {/* Loading Animation */}
          <div className="flex justify-center gap-2">
            <div className="h-2 w-2 rounded-full bg-primary animate-bounce" style={{ animationDelay: '0ms' }}></div>
            <div className="h-2 w-2 rounded-full bg-primary animate-bounce" style={{ animationDelay: '150ms' }}></div>
            <div className="h-2 w-2 rounded-full bg-primary animate-bounce" style={{ animationDelay: '300ms' }}></div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
